import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface Stats {
  total_documents: number;
  total_chunks: number;
  total_queries: number;
  storage_used_bytes: number;
}

const ENDPOINT_URL = (import.meta.env.VITE_API_BASE ?? window.location.origin) + '/v1';

export function DashboardPage() {
  const { tenant } = useAuth();
  const [stats, setStats] = useState<Stats>({ total_documents: 0, total_chunks: 0, total_queries: 0, storage_used_bytes: 0 });
  const [loading, setLoading] = useState(true);

  // API Keys state
  const [keys, setKeys] = useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);
  const [copiedBaseUrl, setCopiedBaseUrl] = useState(false);
  const [copiedNewKey, setCopiedNewKey] = useState(false);
  const [voiceUsage, setVoiceUsage] = useState<{ enabled: boolean; used: number; limit: number; plan: string; voice_base_url: string } | null>(null);

  const loadKeys = () => {
    api.listApiKeys()
      .then((d: any) => setKeys(d.api_keys ?? []))
      .catch((e) => console.error('Failed to load API keys:', e))
      .finally(() => setLoadingKeys(false));
  };

  useEffect(() => {
    Promise.all([
      api.getStats().catch(() => ({ total_documents: 0, total_chunks: 0, total_queries: 0, storage_used_bytes: 0 })),
      api.listApiKeys().catch(() => ({ api_keys: [] })),
      api.getVoiceUsage().catch(() => null)
    ]).then(([statsRes, keysRes, voiceRes]) => {
      setStats(statsRes);
      setKeys(keysRes.api_keys ?? []);
      setVoiceUsage(voiceRes);
    }).finally(() => {
      setLoading(false);
      setLoadingKeys(false);
    });
  }, []);

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await api.createApiKey(newKeyName.trim());
      setRevealed(result);
      setNewKeyName('');
      loadKeys();
    } catch (e) {
      alert(String(e));
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id: string, prefix: string) => {
    if (!confirm(`Revoke key ${prefix}••••••••? This cannot be undone.`)) return;
    try {
      await api.revokeApiKey(id);
      loadKeys();
    } catch (e) {
      alert(String(e));
    }
  };

  const copyBaseUrl = () => {
    navigator.clipboard.writeText(ENDPOINT_URL);
    setCopiedBaseUrl(true);
    setTimeout(() => setCopiedBaseUrl(false), 2000);
  };

  const copyNewKey = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedNewKey(true);
    setTimeout(() => setCopiedNewKey(false), 2000);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const STAT_CARDS = [
    { label: 'Storage Used', value: loading ? '…' : formatBytes(stats.storage_used_bytes), icon: '💾', color: '#34d399' },
    { label: 'Total Documents', value: loading ? '…' : stats.total_documents.toString(), icon: '📁', color: '#a78bfa' },
    { label: 'Chunks Generated', value: loading ? '…' : stats.total_chunks.toLocaleString(), icon: '⚡', color: '#22d3ee' },
    { label: 'RAG Queries Run', value: loading ? '…' : stats.total_queries.toLocaleString(), icon: '🔍', color: '#10b981' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Hệ thống ErixOne DIaaS Engine Core</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid-2" style={{ gap: '16px' }}>
        {STAT_CARDS.map(c => (
          <div key={c.label} className="stat-card">
            <div className="stat-card__label">{c.label}</div>
            <div className="stat-card__value" style={{ color: c.color }}>{c.value}</div>
            <div className="stat-card__icon">{c.icon}</div>
          </div>
        ))}
      </div>

      {/* Client Setup + Instructions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        
        {/* OpenAI Endpoint Config */}
        <div className="card card--accent">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔌</span> OpenAI-Compatible API Endpoint
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Base URL</div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa', background: 'rgba(124,58,237,0.1)', padding: '6px 10px', borderRadius: '6px', flex: 1, wordBreak: 'break-all' }}>
                  {ENDPOINT_URL}
                </code>
                <button className="btn btn--secondary btn--sm" onClick={copyBaseUrl}>
                  {copiedBaseUrl ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Auth Header</div>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#22d3ee', background: 'rgba(6,182,212,0.1)', padding: '6px 10px', borderRadius: '6px', display: 'block', wordBreak: 'break-all' }}>
                Authorization: Bearer sk-one-...
              </code>
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
            Dùng tên model <code style={{ fontFamily: 'var(--font-mono)', color: '#a78bfa' }}>erixone-rag</code> khi gọi API.<br/>
            Cấu hình trong Cursor hoặc LibreChat để hỏi đáp tài liệu trực tiếp từ các IDE/chat client.
          </div>
        </div>

        {/* Erix Voice Quota & Status */}
        <div className="card" style={{ background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)', border: '1px solid rgba(124, 58, 237, 0.25)' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🎙️</span> Erix Voice Quota
            </div>
            <span className={`badge badge--${voiceUsage?.enabled ? 'green' : 'gray'}`}>
              {voiceUsage?.enabled ? 'Đang hoạt động' : 'Chưa kích hoạt'}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Hạn mức tháng này:</span>
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#c084fc' }}>
                {voiceUsage?.limit === -1 ? 'Không giới hạn (∞)' : `${voiceUsage?.used ?? 0} / ${voiceUsage?.limit ?? 100} requests`}
              </span>
            </div>

            {/* Usage progress bar */}
            {voiceUsage?.limit !== -1 && (
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  borderRadius: '6px',
                  background: ((voiceUsage?.used ?? 0) / (voiceUsage?.limit || 1)) > 0.8 ? 'var(--accent-red)' : 'var(--gradient-main)',
                  width: `${Math.min(100, Math.round(((voiceUsage?.used ?? 0) / (voiceUsage?.limit || 100)) * 100))}%`,
                  transition: 'width 0.4s ease'
                }} />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '8px', fontSize: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Gói hiện tại:</span>
              <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{tenant?.plan || 'free'}</span>
            </div>
            
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
              Hỗ trợ nhập giọng nói tiếng Việt trong Chat và kết nối MCP trực tiếp với <code>voice.wowerix.com</code>.
            </div>
          </div>
        </div>

        {/* System Settings & Multi-tenant status */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px' }}>⚙️ Tenant Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Tenant ID:</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{tenant?.id ?? '...'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Hạn mức lưu trữ:</span>
              <span style={{ fontWeight: 600 }}>
                {tenant?.plan === 'enterprise' ? 'Không giới hạn' : tenant?.plan === 'pro' ? '10 GB' : '100 MB'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Ecosystem source:</span>
              <span className="badge badge--green">one.wowerix.com</span>
            </div>
          </div>
        </div>

      </div>

      {/* Revealed Key Modal */}
      {revealed && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div className="card" style={{ maxWidth: '480px', width: '100%', border: '1px solid var(--border-accent)', background: 'var(--bg-surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '20px' }}>🔑</span>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>API Key Created: {revealed.name}</div>
            </div>
            <div className="api-key-reveal" style={{ marginBottom: '12px' }}>{revealed.key}</div>
            <div className="key-warning" style={{ marginBottom: '16px' }}>⚠️ Save this key now — it will never be shown again.</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn--primary" onClick={() => copyNewKey(revealed.key)} style={{ flex: 1, justifyContent: 'center' }}>
                {copiedNewKey ? '✓ Copied!' : 'Copy Key'}
              </button>
              <button className="btn btn--secondary" onClick={() => setRevealed(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Create New API Key */}
      <div className="card">
        <div className="card__title">Create Developer API Key</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            className="input"
            placeholder="Ví dụ: Cursor Integration, Obsidian Sync..."
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createKey()}
            style={{ flex: 1 }}
          />
          <button className="btn btn--primary" onClick={createKey} disabled={creating || !newKeyName.trim()}>
            {creating ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Generate'}
          </button>
        </div>
      </div>

      {/* API Keys List */}
      <div className="card">
        <div className="card__title">Your Developer API Keys</div>
        {loadingKeys ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}><div className="spinner" /></div>
        ) : !keys.length ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
            Chưa tạo API key nào. Tạo mới ở ô phía trên.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tên Key</th>
                  <th>API Key</th>
                  <th>Lần cuối dùng</th>
                  <th>Ngày tạo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 500 }}>{k.name}</td>
                    <td>
                      <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa' }}>
                        {k.key_prefix}••••••••
                      </code>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {k.last_used ? new Date(k.last_used).toLocaleString() : 'Chưa từng dùng'}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button className="btn btn--danger btn--sm" onClick={() => revokeKey(k.id, k.key_prefix)}>
                        Thu hồi
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
